import { logException } from '@lumapps/base-api/utils/logException';
import { parseJson } from '@lumapps/utils/object/parse';

import { DEFAULT_STREAM_EVENT_NAME, EXTRACT_STREAMING_FIELDS_REGEX } from './constants';
import type { StreamError, StreamEvent, StreamFormattedChunk, StreamingResponse } from './types';

/**
 * Check if the data returned by the stream are errors
 */
export function isStreamError<E extends StreamError>(stream: any): stream is StreamFormattedChunk<E> {
    return 'errors' in stream;
}

/**
 * Check if the data returned by the stream are data
 */
export function isStreamData<E extends StreamEvent>(stream: any): stream is StreamFormattedChunk<E> {
    return 'data' in stream;
}

/**
 * Generator function to stream responses from fetch calls.
 */
async function* readStream(reader: ReadableStreamDefaultReader<string>) {
    // Attach Reader
    while (true) {
        // wait for next encoded chunk
        // eslint-disable-next-line no-await-in-loop
        const { done, value } = await reader.read();
        // check if stream is done
        if (done) {
            break;
        }
        // Decodes data chunk and yields it
        yield value;
    }
}

/**
 * Manages a readable stream type response to intercept each chunk, format them, and return them as they are received.
 */
export async function* formatStreamResponse<Events extends StreamEvent = StreamEvent>(
    /**
     * The readable stream to format
     */
    stream: ReadableStream,
): AsyncGenerator<StreamingResponse<Events>> {
    /** Decode the stream as each chunk are encoded in binary by default  */
    const reader = stream.pipeThrough(new TextDecoderStream()).getReader();

    /** Array to store each chunk */
    let streamResponse: StreamingResponse<Events> = {
        reader,
        lastChunk: undefined,
        chunks: [],
    };

    for await (const chunk of readStream(reader)) {
        try {
            /** Extract the event name and data from each chunks */
            const chunkData = chunk.matchAll(EXTRACT_STREAMING_FIELDS_REGEX);

            /** Convert it to an array */
            const chunkDataToArray = Array.from(chunkData);

            /**
             * Reduce the array of chunks to an object that will group each chunk by event name.
             * If no even name is given with a chunk, it will go to a `default` key.
             */
            const lastChunk = chunkDataToArray.reduce(
                (acc, [, fieldName, fieldValue]) => {
                    // Replace an `undefined` event name with the default one
                    const appendDefaultEventName = (eventName: string) => eventName ?? DEFAULT_STREAM_EVENT_NAME;
                    // Parse data and event name
                    const fieldData = fieldName === 'data' ? parseJson(fieldValue) : appendDefaultEventName(fieldValue);

                    return { ...acc, [fieldName]: fieldData };
                },
                { rawChunk: chunk } as StreamFormattedChunk<Events>,
            );

            const previousChunks = streamResponse.chunks || [];
            /** Append the new chunk to the rest of the stream */
            streamResponse = {
                ...streamResponse,
                lastChunk,
                chunks: [...previousChunks, lastChunk],
            };

            /** Submit the chunk to the onChunkDownload callback */
            yield streamResponse;
        } catch (error) {
            logException(error);

            yield streamResponse;
        }
    }

    /** Once the stream ends, return the complete data as response */
    return streamResponse;
}

/** Read the given stream async generator and call the given callback when each chunk is received  */
export const readStreamGenerator = async <Events extends StreamEvent>(
    stream: AsyncGenerator<StreamingResponse<Events>>,
    onChunkDownload: (chunk: StreamingResponse<Events>) => void,
    onError?: (error: unknown) => void,
) => {
    try {
        for await (const chunks of stream) {
            onChunkDownload(chunks);
        }
    } catch (error) {
        // Catch fails (e.g. API GW closes the connection)
        onError?.(error);
    }
};
